import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

const CheckBoxToolBar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedLine,
  onComponentUpdate,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [borderRadiusInput, setBorderRadiusInput] = useState(
    String(selectedLine?.borderRadius ?? 1)
  );
  // Update states when window size changes
  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  useEffect(() => {
    setBorderRadiusInput(String(selectedLine?.borderRadius ?? 1));
  }, [selectedLine?.borderRadius]);
  // Calculate offset for proper centering
  const calculatePosition = () => {
    // For mobile, just center in viewport
    if (windowWidth < 768) {
      return {
        left: "50%",
        transform: "translateX(-50%)",
      };
    }

    // For desktop, account for both sidebars
    let leftOffset = "50%";

    // Calculate sidebar offsets
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0; // 64px / 2

    // Combine offsets
    const totalOffset = leftSidebarOffset + rightSidebarOffset;

    if (totalOffset !== 0) {
      leftOffset = `calc(50% + ${totalOffset}px)`;
    }

    return {
      left: leftOffset,
      transform: "translateX(-50%)",
    };
  };

  const position = calculatePosition();

  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const commitBorderRadius = () => {
    const clamped = clampNumber(borderRadiusInput, 1, 50, 1);
    setBorderRadiusInput(String(clamped));
    if (selectedLine?.borderRadius !== clamped) {
      onComponentUpdate(questionId, selectedLine.id, { borderRadius: clamped });
    }
  };

  const resetBorderRadiusInput = () => {
    setBorderRadiusInput(String(selectedLine?.borderRadius ?? 1));
  };
  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={``}
      >
        <div
          className={`bg-white rounded-xl shadow-xl border border-gray-200 relative ${
            isMobile ? "p-2" : "p-3"
          }`}
          style={{
            width: isMobile ? "calc(100vw - 24px)" : "600px",
            maxWidth: "calc(100vw - 24px)",
            maxHeight: isMobile ? "40vh" : "none",
            overflowY: isMobile ? "auto" : "visible",
          }}
        >
          <button
            onClick={onDeselect}
            className={`${
              isMobile ? "top-2 right-2" : "-top-3 right-2"
            } absolute bg-white rounded-full ${
              isMobile ? "p-1" : "p-1.5"
            } shadow-md border border-gray-200`}
            aria-label="Close"
            title="Close"
          >
            <X size={isMobile ? 14 : 16} className="text-gray-700" />
          </button>
          <div>
            <div
              style={{
                minWidth: 200,
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(auto-fit, minmax(120px, 1fr))"
                  : "repeat(3, minmax(0, 1fr))",
                gap: isMobile ? 8 : 12,
              }}
              className="justify-between"
            >
              {/* State */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700 leading-tight">
                  State:
                </span>
                <select
                  value={selectedLine?.shapeType}
                  onChange={(e) => {
                    onComponentUpdate(questionId, selectedLine.id, {
                      shapeType: e.target.value,
                      correctValue: e.target.value !== "square",
                    });
                  }}
                  className="block h-8 w-full rounded-md border border-gray-300 bg-white py-1.5 px-2 text-[11px] leading-tight text-gray-800 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="cross">Cross</option>
                  <option value="tick">Tick</option>
                  <option value="square">Solid</option>
                </select>
              </label>

              {/* Check Color */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700 leading-tight">
                  Check color:
                </span>
                <input
                  type="color"
                  value={selectedLine?.shapeTypeColor}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      shapeTypeColor: e.target.value,
                    })
                  }
                  className="h-8 w-full rounded border border-gray-300 p-0 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>

              {/* Border Radius */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700 leading-tight">
                  Border Radius:
                </span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={borderRadiusInput}
                  onChange={(e) => {
                    const next = e.target.value;
                    setBorderRadiusInput(next);
                    const parsed = parseInt(next, 10);
                    if (
                      Number.isFinite(parsed) &&
                      parsed >= 1 &&
                      parsed <= 50 &&
                      selectedLine?.borderRadius !== parsed
                    ) {
                      onComponentUpdate(questionId, selectedLine.id, {
                        borderRadius: parsed,
                      });
                    }
                  }}
                  onBlur={commitBorderRadius}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitBorderRadius();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      resetBorderRadiusInput();
                    }
                  }}
                  className="block w-full h-8 rounded-md border border-gray-300 bg-white py-1.5 px-2 text-[11px] leading-tight text-gray-800 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>

              {/* Border Color */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700 leading-tight">
                  Border Color:
                </span>
                <input
                  type="color"
                  value={selectedLine?.borderColor}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      borderColor: e.target.value,
                    })
                  }
                  className="h-8 w-full rounded border border-gray-300 p-0 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>

              {/* Background Color */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700 leading-tight">
                  Background Color:
                </span>
                <input
                  type="color"
                  value={selectedLine?.backgroundColor || "#A7EC94"}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      backgroundColor: e.target.value,
                    })
                  }
                  className="h-8 w-full rounded border border-gray-300 p-0 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CheckBoxToolBar;
